import React, { useCallback, useEffect, useState, type ComponentType } from 'react';
import { Pressable, xcss } from '@atlaskit/primitives';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl } from '@atlassian/jira-intl';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazyForPaint } from '@atlassian/react-loosely-lazy';
import { QuickFindSkeleton as QuickFindSkeletonInternal } from '@atlassian/search-page/quick-find-skeleton';
import { useTopMenus as useTopMenusDI } from '@atlassian/jira-navigation-apps-common/src/controllers/top-menus/index.tsx';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common/src/constants.tsx';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils';
import { expVal } from '@atlassian/jira-feature-experiments';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { useQueryParam, useRouterActions } from '@atlassian/react-resource-router';
import { PACKAGE_NAME } from '../../common/constants.tsx';
import messages from './messages.tsx';
import {
	searchDialogMetric,
	SEARCH_DIALOG_DOWNLOAD_START_MARK,
	SEARCH_DIALOG_DOWNLOAD_END_MARK,
} from './performance-metrics/index.tsx';
import { Skeleton } from './skeleton/index.tsx';
import type { SearchProps as SearchPropsType } from './types.tsx';
import { SkeletonInteractive } from './skeleton/interactive.tsx';
import {
	SearchLoadOnIntentMetricEnd,
	searchLoadOnIntentMetricStartFromSkeleton,
} from './performance-metrics/search-load-on-intent.tsx';
import { buildJiraAdvancedSearchUrl, useQuickFindEnabled, useRovoSearchEnabled } from './utils.tsx';

export const QuickFindSkeleton = ({
	setShowRealExperience,
	injectValue,
	// eslint-disable-next-line react-hooks/react-compiler
	useTopMenus = useTopMenusDI,
}: Pick<SearchPropsType, 'useTopMenus'> & {
	setShowRealExperience?: (showRealExperience: boolean) => void;
	injectValue?: string;
}): React.JSX.Element => {
	// eslint-disable-next-line react-hooks/react-compiler
	const [_, { on: openMenu }] = useTopMenus(MENU_ID.SEARCH);
	const { push } = useRouterActions();
	const onClick = useCallback(() => {
		searchLoadOnIntentMetricStartFromSkeleton();
		setShowRealExperience?.(true);
		openMenu();
	}, [setShowRealExperience, openMenu]);

	const onMouseOver = useCallback(() => {
		setShowRealExperience?.(true);
	}, [setShowRealExperience]);

	const onEnter = useCallback(
		(e: React.KeyboardEvent) => {
			const target = e.target;
			if (target instanceof HTMLInputElement) {
				target.blur();
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				const url = buildJiraAdvancedSearchUrl(target.value, window.location.origin);
				push(url.href);
			}
		},
		[push],
	);

	return (
		<Pressable
			inputMode="search"
			onClick={onClick}
			onMouseOver={onMouseOver}
			onFocus={onClick}
			xcss={quickFindSkeletonStyle}
			testId="atlassian-navigation.ui.search.quickfind-skeleton-wrapper"
		>
			<QuickFindSkeletonInternal onEnter={onEnter} isNav4Enabled injectValue={injectValue} />
		</Pressable>
	);
};

const LoadingSkeleton = () => {
	const { formatMessage } = useIntl();
	return (
		<Skeleton
			placeholder={formatMessage(messages.interactiveSearchSkeletonPlaceholder)}
			ariaLabel={formatMessage(messages.interactiveSearchSkeletonAriaLabel)}
		/>
	);
};

const LoadingSkeletonInteractive = () => {
	const { formatMessage } = useIntl();
	return (
		<SkeletonInteractive
			placeholder={formatMessage(messages.interactiveSearchSkeletonPlaceholder)}
		/>
	);
};

const SearchDialogMetricStart = () => {
	useEffect(() => {
		searchDialogMetric.startFromPageLoad();
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_START_MARK);
	}, []);

	return null;
};

const SearchDialogMetricEnd = () => {
	useEffect(() => {
		searchDialogMetric.mark(SEARCH_DIALOG_DOWNLOAD_END_MARK);
	}, []);

	return null;
};

// exported for testing purposes
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const LazySearch = lazyForPaint<ComponentType<SearchPropsType>>(
	() =>
		import(/* webpackChunkName: "async-atlassian-navigation-search" */ './main').then(
			(m) => m.Search,
		),
	{ ssr: false },
);

export const SearchInner = (props: SearchPropsType): React.JSX.Element => {
	const [injectValue] = useQueryParam('text');
	const isQuickFindEnabled = useQuickFindEnabled();
	const isRovoEnabled = useRovoSearchEnabled();
	const loadingSkeleton = expVal(
		'jfp-magma-concurrent-load-search-on-intent-exp',
		'isEnabled',
		false,
	) ? (
		<LoadingSkeletonExpandable useTopMenus={props.useTopMenus} />
	) : (
		<LoadingSkeleton />
	);

	const skeleton =
		isQuickFindEnabled || isRovoEnabled ? (
			<QuickFindSkeleton useTopMenus={props.useTopMenus} injectValue={injectValue} />
		) : (
			loadingSkeleton
		);

	return (
		<JSErrorBoundary id="search" packageName={PACKAGE_NAME} fallback="unmount">
			<SearchDialogMetricStart />
			<Placeholder name="lazy-search" fallback={skeleton}>
				<LazySearch {...props} />
				<SearchDialogMetricEnd />
				{expVal('jfp-magma-concurrent-load-search-on-intent-exp', 'isEnabled', false) && (
					<SearchLoadOnIntentMetricEnd />
				)}
			</Placeholder>
		</JSErrorBoundary>
	);
};

const loadingSkeletonExpandableWrapperStyle = { width: '100%' } as const; // only for nav4

export const LoadingSkeletonExpandable = ({
	setShowRealExperience,
	// eslint-disable-next-line react-hooks/react-compiler
	useTopMenus = useTopMenusDI,
}: Pick<SearchPropsType, 'useTopMenus'> & {
	setShowRealExperience?: (showRealExperience: boolean) => void;
}): React.JSX.Element => {
	// eslint-disable-next-line react-hooks/react-compiler
	const [_, { on: openMenu }] = useTopMenus(MENU_ID.SEARCH);
	const onClick = useCallback(() => {
		searchLoadOnIntentMetricStartFromSkeleton();
		setShowRealExperience?.(true);
		openMenu();
	}, [setShowRealExperience, openMenu]);

	const onMouseOver = useCallback(() => {
		setShowRealExperience?.(true);
	}, [setShowRealExperience]);
	return (
		// eslint-disable-next-line @atlassian/a11y/interactive-element-not-keyboard-focusable, @atlassian/a11y/no-static-element-interactions
		<div
			data-testid="atlassian-navigation.ui.search.loading-skeleton-expandable"
			onClick={onClick}
			onKeyDown={onClick}
			onMouseOver={onMouseOver}
			onFocus={onClick} // we use onClick instead of onMouseOver to expand the dialog on focus as the interactive skeleton focus expands - if we dont expand we get a flicker
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
			style={loadingSkeletonExpandableWrapperStyle}
		>
			<LoadingSkeletonInteractive />
		</div>
	);
};

export const SearchLoadOnIntent = (props: SearchPropsType): React.JSX.Element => {
	const [injectValue] = useQueryParam('text');
	const isQuickFindEnabled = useQuickFindEnabled();
	// eslint-disable-next-line react-hooks/react-compiler
	const { useTopMenus = useTopMenusDI } = props;
	// eslint-disable-next-line react-hooks/react-compiler
	const [isSearchMenuOpen] = useTopMenus(MENU_ID.SEARCH);
	const [showRealExperience, setShowRealExperience] = useState(isSearchMenuOpen);
	const isRovoEnabled = useRovoSearchEnabled();

	useEffect(() => {
		if (isSearchMenuOpen) {
			setShowRealExperience(true);
		}
	}, [isSearchMenuOpen, setShowRealExperience]);

	return (
		<UFOSegment name="atlassian-navigation-search">
			{showRealExperience ? (
				<UFOSegment name="atlassian-navigation-search-experience">
					<SearchInner {...props} />
				</UFOSegment>
			) : (
				<UFOSegment name="atlassian-navigation-search-loading-skeleton-expandable">
					{isQuickFindEnabled || isRovoEnabled ? (
						<QuickFindSkeleton
							// eslint-disable-next-line react-hooks/react-compiler
							useTopMenus={useTopMenus}
							setShowRealExperience={setShowRealExperience}
							injectValue={injectValue}
						/>
					) : (
						<LoadingSkeletonExpandable
							// eslint-disable-next-line react-hooks/react-compiler
							useTopMenus={useTopMenus}
							setShowRealExperience={setShowRealExperience}
						/>
					)}
				</UFOSegment>
			)}
		</UFOSegment>
	);
};

export const Search = componentWithCondition(
	() => expVal('jfp-magma-concurrent-load-search-on-intent-exp', 'isEnabled', false),
	SearchLoadOnIntent,
	SearchInner,
);

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export type { SearchProps } from './types';

const quickFindSkeletonStyle = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	width: '100%',
	padding: 'space.0',
});
